import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const INDUSTRIES = [
    {
        id: "food",
        label: "Food & Beverage Industry",
        image:
            "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=900&q=74",
    },
    {
        id: "fmcg",
        label: "Fast-Moving Consumer Goods (FMCG)",
        image:
            "https://images.unsplash.com/photo-1580674285054-bed31e145f59?auto=format&fit=crop&w=900&q=74",
    },
    {
        id: "halal",
        label: "Halal Industry",
        image:
            "https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55?auto=format&fit=crop&w=900&q=74",
    },
];

const SEGMENTS = [
    "Retail & Wholesale Distribution",
    "Private Label & Brand Owners",
    "E-Commerce Businesses",
    "Importers & Exporters",
    "Supply Chain & Logistics Ecosystem",
];

export const Industries = () => {
    const [active, setActive] = useState(INDUSTRIES[0]);

    return (
        <section
            id="industries"
            data-testid="industries-section"
            className="relative bg-[#0B0807]/82 backdrop-blur-md py-24 sm:py-32 lg:py-40 px-4 sm:px-8 lg:px-12 border-t border-white/5"
        >
            <div className="flex items-center gap-3 font-mono-text text-[10px] sm:text-xs uppercase tracking-[0.3em] text-zinc-500 mb-6">
                <span className="h-px w-10 bg-[#D69331]" />
                <span>04 / Industries</span>
            </div>

            <motion.h2
                initial={{ y: 60, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8 }}
                className="font-display uppercase font-bold leading-[0.9] tracking-tight text-white text-4xl sm:text-6xl lg:text-7xl max-w-5xl"
            >
                Delivering value across{" "}
                <span className="text-zinc-600">
                    multiple industries & market segments.
                </span>
            </motion.h2>

            <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
                {/* Hover list */}
                <div className="lg:col-span-7">
                    <ul className="divide-y divide-white/10 border-t border-b border-white/10">
                        {INDUSTRIES.map((ind, i) => (
                            <li
                                key={ind.id}
                                onMouseEnter={() => setActive(ind)}
                                onFocus={() => setActive(ind)}
                                className="group"
                            >
                                <a
                                    href="#contact"
                                    data-testid={`industry-${ind.id}`}
                                    className={`flex items-center justify-between gap-6 py-6 sm:py-8 transition-colors ${
                                        active.id === ind.id
                                            ? "text-white"
                                            : "text-zinc-500"
                                    }`}
                                >
                                    <span className="flex items-baseline gap-4 sm:gap-6">
                                        <span className="font-mono-text text-[10px] uppercase tracking-[0.3em] text-zinc-600">
                                            0{i + 1}
                                        </span>
                                        <span className="font-display uppercase text-2xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
                                            {ind.label}
                                        </span>
                                    </span>
                                    <ArrowUpRight
                                        size={22}
                                        className={`shrink-0 transition-transform ${
                                            active.id === ind.id
                                                ? "translate-x-0 -translate-y-0 text-[#D69331]"
                                                : "-translate-x-1 translate-y-1"
                                        }`}
                                    />
                                </a>
                            </li>
                        ))}
                    </ul>

                    <div className="mt-10">
                        <p className="font-mono-text text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">
                            Market Segments
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {SEGMENTS.map((s) => (
                                <span
                                    key={s}
                                    className="px-3 py-2 border border-white/15 text-zinc-300 font-mono-text text-[11px] uppercase tracking-[0.15em]"
                                >
                                    {s}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Preview image */}
                <div className="lg:col-span-5 lg:sticky lg:top-32">
                    <div className="relative aspect-[4/5] w-full overflow-hidden border border-white/10 bg-[#0a0a0a]">
                        <AnimatePresence mode="wait">
                            <motion.img
                                key={active.id}
                                src={active.image}
                                alt={active.label}
                                initial={{ opacity: 0, scale: 1.05 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 1.02 }}
                                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                                className="absolute inset-0 w-full h-full object-cover"
                                loading="lazy"
                            />
                        </AnimatePresence>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-6 flex items-end justify-between gap-4">
                            <div>
                                <p className="font-mono-text text-[10px] uppercase tracking-[0.3em] text-[#D69331] mb-2">
                                    Now showing
                                </p>
                                <p className="font-display uppercase text-lg sm:text-xl text-white">
                                    {active.label}
                                </p>
                            </div>
                            <span className="font-mono-text text-xs text-zinc-400">
                                0
                                {INDUSTRIES.findIndex(
                                    (i) => i.id === active.id,
                                ) + 1}
                                /{INDUSTRIES.length}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
